import DarkModeIcon from '@mui/icons-material/DarkMode';
import LanguageIcon from '@mui/icons-material/Language';
import LightModeIcon from '@mui/icons-material/LightMode';
import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { useThemeMode } from '../../theme';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import NotificationCenter from '../NotificationCenter/NotificationCenter';

export default function AdminHeader({ isMobile, onMenuClick, onAvatarClick }) {
  const { user } = useAuth();
  const { t, locale, changeLocale } = useI18n();
  const { toggleMode, isDark } = useThemeMode();
  const navigate = useNavigate();

  const isSuperAdmin = user?.role === 'super_admin';

  const [moreAnchorEl, setMoreAnchorEl] = React.useState(null);
  const [languageAnchorEl, setLanguageAnchorEl] = React.useState(null);

  const moreOpen = Boolean(moreAnchorEl);
  const openMore = (event) => setMoreAnchorEl(event.currentTarget);
  const closeMore = () => setMoreAnchorEl(null);

  const languageOpen = Boolean(languageAnchorEl);
  const openLanguage = (event) => setLanguageAnchorEl(event.currentTarget);
  const closeLanguage = () => setLanguageAnchorEl(null);

  const LANGUAGES = [
    { key: 'ru', label: 'Русский' },
    { key: 'en', label: 'English' },
    { key: 'ky', label: 'Кыргызча' },
  ];

  return (
    <AppBar position="sticky" elevation={0} color="default">
      <Toolbar sx={{ gap: 1 }}>
        {isMobile && (
          <IconButton
            onClick={onMenuClick}
            edge="start"
            aria-label={t('nav.openMenu', 'Open menu')}
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            flexGrow: 1,
            color: isSuperAdmin ? 'warning.main' : 'inherit',
          }}
        >
          {isSuperAdmin ? t('nav.superAdmin') : t('nav.adminPanel')}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={isDark ? t('theme.lightTheme') : t('theme.darkTheme')}>
            <IconButton
              onClick={toggleMode}
              aria-label={t('theme.toggle', 'Toggle theme')}
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              {isDark ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          {!isMobile ? (
            <>
              <NotificationCenter />
              <LanguageSwitcher />
            </>
          ) : (
            <>
              <IconButton
                onClick={openMore}
                aria-label={t('nav.more', 'More')}
                aria-controls={moreOpen ? 'admin-header-more-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={moreOpen ? 'true' : undefined}
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <MoreVertIcon />
              </IconButton>

              <Menu
                id="admin-header-more-menu"
                anchorEl={moreAnchorEl}
                open={moreOpen}
                onClose={closeMore}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={() => {
                    closeMore();
                    navigate('/notifications');
                  }}
                >
                  <ListItemIcon>
                    <NotificationsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('nav.notifications', 'Notifications')}
                  />
                </MenuItem>

                <MenuItem
                  onClick={(event) => {
                    // Open a dedicated submenu instead of nesting LanguageSwitcher.
                    openLanguage(event);
                  }}
                >
                  <ListItemIcon>
                    <LanguageIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t('nav.language', 'Language')} />
                </MenuItem>
              </Menu>

              <Menu
                id="admin-header-language-menu"
                anchorEl={languageAnchorEl}
                open={languageOpen}
                onClose={closeLanguage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {LANGUAGES.map((lang) => (
                  <MenuItem
                    key={lang.key}
                    selected={locale === lang.key}
                    onClick={() => {
                      changeLocale(lang.key);
                      closeLanguage();
                      closeMore();
                    }}
                  >
                    {lang.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}

          <IconButton
            onClick={onAvatarClick}
            aria-label={t('profile.open', 'Open profile')}
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <Avatar
              src={user?.avatar}
              sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
            >
              {!user?.avatar &&
                `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`}
            </Avatar>
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
